/**
 * normalizeConfig — translate the public ChartConfig into the internal
 * NormalizedChartModel every chart's buildTraces consumes.
 *
 * Resolves the data source (inline `data` or a `datasetId` looked up via the
 * provided resolver), applies `filters` as a data-subset step, and settles
 * encoding / colors / labels into one place. This is where the single contract
 * is enforced, so each chart module can stay small and uniform.
 */

import type { DataPoint } from '../types/dashboard';
import type { ChartConfig, NormalizedChartModel } from './model';
import { applyFilters } from './shared/filters';
import { resolveLevelView } from './shared/levelView';

/** Resolves a `datasetId` to its rows. Returns [] for unknown ids. */
export type DatasetResolver = (id: string) => DataPoint[];

function resolveData(config: ChartConfig, getDataset?: DatasetResolver): DataPoint[] {
  if (config.data && config.data.length > 0) return config.data;
  if (config.datasetId && getDataset) return getDataset(config.datasetId);
  return [];
}

export function normalizeConfig(
  config: ChartConfig,
  getDataset?: DatasetResolver
): NormalizedChartModel {
  const resolved = resolveData(config, getDataset);
  const data = applyFilters(resolved, config.filters);

  // A plan view describes the level it is showing (bench, n, accuracy) and counts its
  // legend entries over it. Resolved here rather than by the card, so the report and
  // the PNG export — which render through ChartRenderer with no card around them —
  // carry the same readouts. The level is the chart's first categorical filter.
  const levelFilter = config.filters?.find(
    (f): f is Extract<typeof f, { kind: 'categorical' }> => f.kind === 'categorical'
  );
  const levelView = resolveLevelView({
    data,
    allData: resolved,
    level: levelFilter?.values?.[0],
    groupKey: config.encoding?.groupKey,
    options: config.options,
  });

  return {
    data,
    // Only when filtering actually removed rows: a chart that draws a subset can then
    // scale itself against the whole (the plan view ranges its axes over this so the
    // frame doesn't move when the shown bench changes — US 2097). Comparing lengths is
    // enough; applyFilters returns `resolved` itself for a no-op.
    ...(data.length !== resolved.length ? { unfilteredData: resolved } : {}),
    encoding: config.encoding ?? {},
    series: config.series ?? [],
    groupColors: config.groupColors ?? {},
    labels: {
      title: config.title,
      // The shown bench joins the authored subtitle, and the block count + accuracy
      // head the legend (see shared/levelView). Both track the level, so both change
      // when the bench changes — the trade that buys back `constrain: 'domain'` and a
      // frame that holds still.
      subtitle: config.subtitle,
      ...(levelView.benchLabel ? { benchLabel: levelView.benchLabel } : {}),
      ...(levelView.legendHeading ? { legendHeading: levelView.legendHeading } : {}),
      xAxisLabel: config.xAxisLabel,
      yAxisLabel: config.yAxisLabel,
      y2AxisLabel: config.y2AxisLabel,
    },
    limits: config.limits,
    // Whatever the consumer authored, untouched: the level's own readouts no longer
    // travel as statistics (they are the subtitle and the legend heading now).
    ...(config.statistics ? { statistics: config.statistics } : {}),
    options: levelView.legendCounts
      ? { ...config.options, legendCounts: levelView.legendCounts }
      : config.options,
  };
}
